/**
 * The field sweep, read by the client.
 *
 * A conjecture pass reasons from memory, and memory is the consensus as of a
 * cutoff: the graph knows its own contradictions and gaps, not where the field
 * is unsettled now. A sweep is the one searched pass that asks that — where
 * the literature on this node's subject is open, disputed, failing to
 * replicate, or recently moved — and reports it in the words of pages the
 * search returned.
 *
 * Reportage, never structure. No item becomes a node on its own; an item is
 * what one page says, and it reaches reasoning only as quoted data in a
 * conjecture pass's turn, with its host beside it. The quotes are text an
 * attacker can reach, so they are framed as data wherever they are printed,
 * and a conjecture resting only on one site's pages says so on its card.
 *
 * The source check's discipline, applied: the citation gate (`matchSeen`) and
 * the quote check (`quoteFoundOn`), both imported, never restated. An item whose
 * quote is not on the page the search returned is deleted and counted — the
 * one place this app deletes on a failed quote, because an anomaly nobody said
 * is the worst thing a conjecture pass could be handed, and a sweep's items
 * are handed to one. A quote that could not be checked is kept, and says so.
 *
 * Pure: no store, no clock, no model.
 */

import type { FieldSweep, ModelStamp, ReasonNode, Session, SweepItem } from './types'
import { canonicalUrl, isHttpUrl, registrableHost } from './url'
import { fnv1a64, foldText, quoteFoundOn } from './similarity'
import { SOURCE_QUOTE_CHARS } from './textRetention'
import { calendarYear } from './dates'
import { failureNote, matchSeen } from '../engine/providers/contract'
import type { SearchOutcome, SeenSource } from '../engine/providers/contract'

/* ── the budget and the bounds ─────────────────────────────────────────── */

/** Searches one sweep may run, and pages it may open. Four to six queries is
 *  what the brief asks for; the bound is the top of that. */
export const SWEEP_MAX_SEARCHES = 6
export const SWEEP_MAX_FETCHES = 4

/** Items one sweep keeps. Beyond this a sweep is a bibliography. */
export const SWEEP_MAX_ITEMS = 8

/** Items a conjecture pass is shown, across every sweep in reach. */
export const SWEEP_BLOCK_MAX = 8

/** Sweeps a session keeps, oldest dropped first. */
export const SWEEP_HISTORY = 24

/* ── the kinds ─────────────────────────────────────────────────────────── */

/** What an item reports, as a VALUE — `persist.ts` validates against it. */
export const SWEEP_KINDS = [
  'open-question',
  'anomaly',
  'dispute',
  'failed-replication',
  'recent-shift',
] as const

export type SweepKind = (typeof SWEEP_KINDS)[number]

// How each kind reads in a sentence, each naming what the PAGE reports. A kind is
// a status word — what an item is, the way a source's design is — so the table is
// defined in core/vocabulary.ts and re-exported here for every surface that reads
// the rest of a sweep's words from this file (D287).
export { SWEEP_KIND_LABEL } from './vocabulary'

/* ── the dependency cone ───────────────────────────────────────────────── */

/**
 * A node and every premise it rests on: its parent, its bridge parents, and
 * theirs, up to the seed. The walk `buildSourceRecordBlock` makes, and the one a
 * sweep's reach is defined over: a sweep keyed to a node is read by a
 * conjecture pass on any node whose cone contains it.
 */
export function dependencyCone(session: Pick<Session, 'nodes'>, targetId: string): ReasonNode[] {
  const out: ReasonNode[] = []
  const stack = [targetId]
  const seen = new Set<string>()
  while (stack.length) {
    const id = stack.pop()!
    if (seen.has(id)) continue
    seen.add(id)
    const node = session.nodes[id]
    if (!node) continue
    out.push(node)
    if (node.parentId) stack.push(node.parentId)
    for (const parent of node.bridgeFrom ?? []) stack.push(parent)
  }
  return out
}

/* ── one sweep, read ──────────────────────────────────────────────────── */

/** The fingerprint of the instructions a sweep ran under: exact bytes. */
export const sweepInstrument = (system: string): string => fnv1a64(system)

const KIND_OK = new Set<string>(SWEEP_KINDS)

const str = (v: unknown, max: number): string =>
  typeof v === 'string' ? v.trim().slice(0, max) : ''

const yearOf = (v: unknown, now: number): number | undefined => {
  if (typeof v !== 'number' || !Number.isInteger(v)) return undefined
  const latest = calendarYear(now) + 1
  return v >= 1000 && v <= latest ? v : undefined
}

function distinctPages(pages: readonly SeenSource[]): number {
  return new Set(pages.map((p) => canonicalUrl(p.url))).size
}

export interface SweepInput {
  /** The `items` objects the response streamed, as the model wrote them. */
  raw: readonly unknown[]
  search: SearchOutcome | undefined
  truncated: boolean
  /** The sweep's own id; each item's id is built from it. */
  id: string
  node: Pick<ReasonNode, 'id' | 'label' | 'claim'>
  model: ModelStamp
  instrument: string
  now: number
}

/**
 * One response → the sweep the app keeps.
 *
 * EXPORTED FOR THE EVAL SEAM as well as for `reason.ts`, for the reason every
 * admission function here is: a harness carrying its own copy would measure it.
 */
export function assessSweep(input: SweepInput): FieldSweep {
  const pages = input.search?.sources ?? []
  const items: SweepItem[] = []
  const keptKeys = new Set<string>()
  let dropped = 0
  let unquoted = 0
  let malformed = 0
  let overCeiling = 0
  for (const raw of input.raw) {
    // Counted, never swallowed: a paid-for item past the ceiling is still one
    // the reader is owed the number of.
    if (items.length >= SWEEP_MAX_ITEMS) {
      overCeiling++
      continue
    }
    const o = (raw ?? {}) as Record<string, unknown>
    const kind = str(o.kind, 40)
    const statement = str(o.statement, 600)
    const quote = typeof o.quote === 'string' ? o.quote.trim() : ''
    if (!KIND_OK.has(kind) || !statement || !quote) {
      malformed++
      continue
    }
    const url = str(o.url, 2000)
    const hit = url && isHttpUrl(url) ? matchSeen(url, pages) : null
    // THE gate, as everywhere: a page the search never returned is not a page.
    if (!hit) {
      dropped++
      continue
    }
    const page = hit.via === 'fetch' && hit.text !== undefined ? foldText(hit.text) : undefined
    const found = quoteFoundOn(page, quote, hit.truncated)
    // Deleted, not demoted. See the header: this item would be handed to a
    // reasoning pass as something a page said, and the page does not say it.
    if (found === false) {
      unquoted++
      continue
    }
    // One item per page: a sweep's items are one page each, and two items
    // resting on one page are one page's account counted twice.
    const key = canonicalUrl(hit.url)
    if (keptKeys.has(key)) {
      malformed++
      continue
    }
    keptKeys.add(key)
    const year = yearOf(o.year, input.now)
    items.push({
      id: `${input.id}.${items.length + 1}`,
      kind: kind as SweepKind,
      statement,
      url: hit.url,
      title: hit.title,
      quote: quote.slice(0, SOURCE_QUOTE_CHARS),
      ...(year !== undefined ? { year } : {}),
      via: hit.via,
      ...(found === true ? { quoteFound: true as const } : {}),
    })
  }

  const partial = !!input.search?.exhausted || input.truncated
  const failures = [
    ...(input.search ? [] : ['Nothing was searched.']),
    ...(input.search?.failures ?? []).map((f) => failureNote(f.code)),
    ...(input.search?.exhausted ? ['The search was cut off before it finished.'] : []),
    ...(input.truncated
      ? ['The response stopped at its length cap, so anything reported after the cut was lost.']
      : []),
  ]
  return {
    id: input.id,
    nodeId: input.node.id,
    subjectAtSweep: `${input.node.label} — ${input.node.claim}`,
    items,
    queries: [...(input.search?.queries ?? [])],
    seen: distinctPages(pages),
    dropped,
    unquoted,
    malformed,
    overCeiling,
    ...(failures.length ? { failures: [...new Set(failures)] } : {}),
    ...(partial ? { partial: true } : {}),
    ...(input.search ? { quotesCheckable: input.search.pageText } : {}),
    searchedAt: input.now,
    model: input.model,
    instrument: input.instrument,
  }
}

/** A session's sweeps with one more, the oldest dropped past the history. */
export function withSweep(sweeps: readonly FieldSweep[] | undefined, s: FieldSweep): FieldSweep[] {
  return [...(sweeps ?? []), s].slice(-SWEEP_HISTORY)
}

/* ── what a conjecture pass is shown ───────────────────────────────────── */

/** One item as a conjecture pass sees it: under a short label it can anchor to. */
export interface ReachedItem {
  /** `S1`, `S2`… — what the block prints and an anchor may name. */
  label: string
  item: SweepItem
  sweep: FieldSweep
}

export interface SweepReach {
  items: ReachedItem[]
  /** Printed label → the item's stable id, for admission to resolve anchors. */
  aliases: Map<string, string>
}

/**
 * The sweep items a conjecture pass on `targetId` is shown, labelled.
 *
 * Every sweep keyed to a node in the target's dependency cone, newest first,
 * items in their sweep's order, capped at `SWEEP_BLOCK_MAX`. `pin` puts one
 * item first whatever the cap — the item a person pressed "Propose what
 * explains this" on has to be in the block it is asked about. Deterministic
 * over the session, so the block and the admission that resolves its labels
 * read the same list.
 */
export function sweepReach(
  session: Pick<Session, 'nodes' | 'sweeps'>,
  targetId: string,
  pin?: string,
): SweepReach {
  const cone = new Set(dependencyCone(session, targetId).map((n) => n.id))
  const inReach = (session.sweeps ?? [])
    .filter((s) => cone.has(s.nodeId))
    .sort((a, b) => b.searchedAt - a.searchedAt)
  const all = inReach.flatMap((sweep) => sweep.items.map((item) => ({ item, sweep })))
  const pinned = pin ? all.filter((x) => x.item.id === pin) : []
  const rest = all.filter((x) => x.item.id !== pin)
  const chosen = [...pinned, ...rest].slice(0, SWEEP_BLOCK_MAX)
  const items = chosen.map((x, i) => ({ label: `S${i + 1}`, item: x.item, sweep: x.sweep }))
  return { items, aliases: new Map(items.map((x) => [x.label, x.item.id])) }
}

/** A sweep item by its stable id, and the sweep it came from. */
export function sweepItemById(
  session: Pick<Session, 'sweeps'>,
  id: string,
): { item: SweepItem; sweep: FieldSweep } | undefined {
  for (const sweep of session.sweeps ?? []) {
    const item = sweep.items.find((i) => i.id === id)
    if (item) return { item, sweep }
  }
  return undefined
}

/** The newest sweep keyed to a node. */
export function latestSweep(
  session: Pick<Session, 'sweeps'>,
  nodeId: string,
): FieldSweep | undefined {
  return (session.sweeps ?? [])
    .filter((s) => s.nodeId === nodeId)
    .sort((a, b) => b.searchedAt - a.searchedAt)[0]
}

/**
 * The one site every sweep item a conjecture offers to explain came from, when
 * there is exactly one — or null.
 *
 * Only when EVERY anchor is a sweep item: a conjecture that also explains a
 * record on the graph rests on more than one page's account. Registrable
 * domains, the source check's own unit of independence.
 */
export function oneSiteAnchors(
  session: Pick<Session, 'sweeps'>,
  anchors: readonly string[],
): string | null {
  if (!anchors.length) return null
  const hosts = new Set<string>()
  for (const a of anchors) {
    const hit = sweepItemById(session, a)
    if (!hit) return null
    hosts.add(registrableHost(hit.item.url))
  }
  return hosts.size === 1 ? [...hosts][0]! : null
}

/* ── the words ─────────────────────────────────────────────────────────── */

/**
 * What a sweep came back with, as one sentence — including the empty case,
 * which is a record of a search and never a statement that the field is settled.
 */
export function sweepSentence(s: FieldSweep): string {
  const q = s.queries.length
  const n = s.items.length
  const tail =
    s.partial || s.failures?.length
      ? ' A search step failed or was cut off, so what it did not return says nothing.'
      : ''
  return n
    ? `${n} unsettled point${n === 1 ? '' : 's'} reported, each resting on one page the search returned and quoting it, after ${q} quer${q === 1 ? 'y' : 'ies'}.${tail}`
    : `Nothing came back that a page could be quoted as calling unsettled, after ${q} quer${q === 1 ? 'y' : 'ies'}. That is a record of this search, not a statement that the field is settled.${tail}`
}
